/**
 * AI CONTEXT: micro-ux - Faint shopping bags burst animation
 * Journal: J-20250902-01
 * 
 * Purpose: Provide a subtle celebratory animation that matches the Tolbert palette
 * and can be triggered near interactive commerce UI (add-to-cart, checkout).
 * 
 * Usage:
 * <BurstBags triggerKey={someChangingValue} className="absolute inset-0" />
 * - The component renders transient particles positioned relative to its container.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

const COLORS = [
  'text-theme-silver',
  'text-bronze',
  'text-gunmetal-gray',
  'text-silver',
  'text-dark-bronze',
];

const ICON_PATH = (
  <path d="M6 7V6a3 3 0 1 1 6 0v1h1.5A1.5 1.5 0 0 1 15 8.5v6A1.5 1.5 0 0 1 13.5 16h-9A1.5 1.5 0 0 1 3 14.5v-6A1.5 1.5 0 0 1 4.5 7H6Zm1 0h4V6a2 2 0 1 0-4 0v1Z" />
);

function createParticles(count) {
  const particles = [];
  for (let i = 0; i < count; i += 1) {
    const angle = (Math.PI * 2 * i) / count;
    const distance = 40 + Math.random() * 60; // px
    const tx = Math.cos(angle) * distance;
    const ty = Math.sin(angle) * distance * -1; // bias upward
    const rot = (Math.random() * 40 - 20).toFixed(1) + 'deg';
    const size = 10 + Math.round(Math.random() * 10);
    const delay = Math.round(Math.random() * 60);
    const color = COLORS[i % COLORS.length];
    particles.push({ id: i, tx, ty, rot, size, delay, color });
  }
  return particles;
}

const BurstBags = ({ triggerKey, className = '' }) => {
  const [burstKey, setBurstKey] = useState(0);
  const particles = useMemo(() => createParticles(9), []);
  const timerRef = useRef(null);

  useEffect(() => {
    // retrigger burst when triggerKey changes
    setBurstKey((k) => k + 1);
    const currentTimer = timerRef.current;
    return () => { if (currentTimer) clearTimeout(currentTimer); };
  }, [triggerKey]);

  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden>
      <div className="relative w-full h-full">
        {particles.map((p) => (
          <span
            key={`${burstKey}-${p.id}`}
            className={`absolute inline-flex items-center justify-center ${p.color}`}
            style={{
              left: '50%',
              top: '50%',
              width: p.size,
              height: p.size,
              transform: 'translate(-50%, -50%)',
              '--tx': `${p.tx}px`,
              '--ty': `${p.ty}px`,
              '--rot': p.rot,
              animation: `bag-burst 900ms ease-out ${p.delay}ms forwards`,
              opacity: 0,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              width={p.size}
              height={p.size}
              className="drop-shadow"
            >
              {ICON_PATH}
            </svg>
          </span>
        ))}
      </div>
    </div>
  );
};

export default BurstBags;


